import { supabase } from '../lib/supabase';

/**
 * The hotel / connector / SaaS graph behind the internal extranet.
 *
 * The three listings never surfaced the relationships the schema already holds:
 * `integrations` links hotels to SaaS accounts, `credentials` links hotels to their
 * PMS connector.
 *
 * The two sides are fetched differently on purpose:
 *
 * - SaaS links come straight from `integrations` / `saas_details`, which internal
 *   users can already read (see 20260209000002_internal_create_integration).
 * - Connector links come from the `internal_integration_graph_secure` view, because
 *   `credentials` holds PMS API keys and must not be exposed to the client. Until
 *   that migration is applied the connector side is simply unavailable, and the rest
 *   of the graph still works.
 */

export interface ConnectorLink {
  code: string;
  name: string;
  status: string | null;
  is_primary: boolean;
  connector_hotel_id: string | null;
}

export interface SaasLink {
  id: string;
  company_name: string;
  status: string | null;
}

export interface HotelNode {
  hotel_id: string;
  hotel_name: string;
  hotel_code: string | null;
  connectors: ConnectorLink[];
  saas_accounts: SaasLink[];
}

/** A hotel as seen from a connector or a SaaS account. */
export interface HotelLink {
  id: string;
  name: string;
  code: string | null;
  /** Integration status, only meaningful when reached from a SaaS account. */
  status?: string | null;
}

export interface IntegrationGraph {
  hotels: HotelNode[];
  byHotelId: Map<string, HotelNode>;
  /** saas_details.id -> the hotels it is integrated with */
  hotelsBySaasId: Map<string, HotelLink[]>;
  /** connectors.code -> the hotels running on it */
  hotelsByConnectorCode: Map<string, HotelLink[]>;
  /**
   * False when the connector side could not be read (view not deployed). The SaaS
   * side is still complete: the screens must not report "no connector" in that case.
   */
  connectorsAvailable: boolean;
}

export const EMPTY_INTEGRATION_GRAPH: IntegrationGraph = {
  hotels: [],
  byHotelId: new Map(),
  hotelsBySaasId: new Map(),
  hotelsByConnectorCode: new Map(),
  connectorsAvailable: false,
};

function toArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function pushLink(index: Map<string, HotelLink[]>, key: string, link: HotelLink): void {
  const existing = index.get(key);
  if (existing) {
    existing.push(link);
  } else {
    index.set(key, [link]);
  }
}

function hotelDisplayName(row: { company_name?: string | null; hotel_name?: string | null; hotel_code?: string | null }): string {
  return (
    (row.company_name ?? '').trim() ||
    (row.hotel_name ?? '').trim() ||
    (row.hotel_code ?? '').trim() ||
    '—'
  );
}

/**
 * Connector links, keyed by hotel id. Returns null when the view is not deployed,
 * which the caller reports as "unavailable" rather than "none".
 */
async function fetchConnectorsByHotel(): Promise<Map<string, ConnectorLink[]> | null> {
  const { data, error } = await supabase
    .from('internal_integration_graph_secure')
    .select('hotel_id, connectors');

  if (error) {
    console.warn(
      '[IntegrationGraph] Connector relations unavailable — is migration ' +
        '20260827000002_internal_integration_graph applied?',
      error.message,
    );
    return null;
  }

  const byHotel = new Map<string, ConnectorLink[]>();
  for (const row of data ?? []) {
    byHotel.set(row.hotel_id, toArray<ConnectorLink>(row.connectors));
  }
  return byHotel;
}

export async function fetchIntegrationGraph(): Promise<IntegrationGraph> {
  const [hotelsRes, integrationsRes, saasRes, connectorsByHotel] = await Promise.all([
    supabase.from('hotel_details').select('id, company_name, hotel_code'),
    supabase.from('integrations').select('hotel_id, saas_id, status'),
    supabase.from('saas_details').select('id, company_name'),
    fetchConnectorsByHotel(),
  ]);

  if (hotelsRes.error) throw hotelsRes.error;
  if (integrationsRes.error) throw integrationsRes.error;
  if (saasRes.error) throw saasRes.error;

  const saasById = new Map<string, string>(
    (saasRes.data ?? []).map(row => [row.id, row.company_name ?? '—']),
  );

  const saasByHotel = new Map<string, SaasLink[]>();
  for (const integration of integrationsRes.data ?? []) {
    const companyName = saasById.get(integration.saas_id);
    // An integration pointing at a deleted SaaS account has nothing to show.
    if (!companyName) continue;

    const links = saasByHotel.get(integration.hotel_id);
    const link: SaasLink = {
      id: integration.saas_id,
      company_name: companyName,
      status: integration.status ?? null,
    };
    if (links) {
      links.push(link);
    } else {
      saasByHotel.set(integration.hotel_id, [link]);
    }
  }

  const rows: HotelNode[] = (hotelsRes.data ?? []).map(row => ({
    hotel_id: row.id,
    hotel_name: hotelDisplayName(row),
    hotel_code: row.hotel_code ?? null,
    connectors: connectorsByHotel?.get(row.id) ?? [],
    saas_accounts: saasByHotel.get(row.id) ?? [],
  }));

  rows.sort((a, b) => a.hotel_name.localeCompare(b.hotel_name));

  return indexIntegrationGraph(rows, connectorsByHotel !== null);
}

export function indexIntegrationGraph(
  rows: HotelNode[],
  connectorsAvailable: boolean,
): IntegrationGraph {
  const byHotelId = new Map<string, HotelNode>();
  const hotelsBySaasId = new Map<string, HotelLink[]>();
  const hotelsByConnectorCode = new Map<string, HotelLink[]>();

  for (const hotel of rows) {
    byHotelId.set(hotel.hotel_id, hotel);

    for (const connector of hotel.connectors) {
      pushLink(hotelsByConnectorCode, connector.code, {
        id: hotel.hotel_id,
        name: hotel.hotel_name,
        code: hotel.hotel_code,
      });
    }

    for (const saas of hotel.saas_accounts) {
      pushLink(hotelsBySaasId, saas.id, {
        id: hotel.hotel_id,
        name: hotel.hotel_name,
        code: hotel.hotel_code,
        status: saas.status,
      });
    }
  }

  // Alphabetical so the chips a user sees are stable between reloads.
  for (const links of hotelsBySaasId.values()) links.sort((a, b) => a.name.localeCompare(b.name));
  for (const links of hotelsByConnectorCode.values()) {
    links.sort((a, b) => a.name.localeCompare(b.name));
  }

  return {
    hotels: rows,
    byHotelId,
    hotelsBySaasId,
    hotelsByConnectorCode,
    connectorsAvailable,
  };
}
